import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrayFieldValue, GroupedFieldValues } from '../../types'
import useLocationState from '../useLocationState'
import { YesObject } from '@yes.technology/react-toolkit'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import {
  LocationStateType,
  PersistentSelection
} from '../useLocationState/useLocationState'
import deepCopy from 'shared/utils/object/deepCopy'

export const SELECT_AND_LINK_PARAM = 'select_and_link'

export const SELECT_AND_LINK_POST_MESSAGE_TYPE = 'select_and_link'

export const CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE = 'cancel_select_and_link'

export const DEFAULT_TARGET_FIELD = '__internal_default_target_field'

export const getPersistentSelection = ({
  targetField,
  targetGroup,
  targetComponent,
  previousPersistentSelection = {},
  groupedFieldValues,
  values
}: {
  previousPersistentSelection?: PersistentSelection
  targetField?: string
  targetGroup?: string
  targetComponent?: LocationStateType['targetComponent']
  groupedFieldValues?: GroupedFieldValues
  values: YesObject[]
}): LocationStateType['persistentSelection'] => {
  const persistentSelection: PersistentSelection = deepCopy(
    previousPersistentSelection
  )

  if (groupedFieldValues) {
    persistentSelection.FilterFields = deepCopy(groupedFieldValues)
  }

  if (targetComponent === 'ObjectView') {
    if (targetField) {
      persistentSelection.ObjectView = {
        ...persistentSelection.ObjectView,
        [targetField]: values
      }
    }

    return persistentSelection
  }

  if (
    targetField &&
    targetGroup &&
    persistentSelection.FilterFields?.[targetGroup][targetField]
  ) {
    persistentSelection.FilterFields[targetGroup][targetField].value = values
  }

  return persistentSelection
}

export const usePersistentSelection = () => {
  const navigateMergingState = useNavigateMergingState<LocationStateType>()
  const { locationState } = useLocationState()

  const [searchParams] = useSearchParams()

  const shouldRenderSelectAndLink = () => {
    const enabledInUrl = searchParams.has(SELECT_AND_LINK_PARAM)

    return !!(locationState?.sitemodelHistory?.length || enabledInUrl)
  }

  const location = useLocation()

  const getRedirectToObjectclassCallback = ({
    targetField,
    targetGroup,
    objectclassUuid,
    filterState,
    targetComponent,
    selectedOptions
  }: {
    targetField?: string
    targetGroup?: string
    objectclassUuid: string
    filterState?: GroupedFieldValues
    targetComponent: LocationStateType['targetComponent']
    selectedOptions?: ArrayFieldValue
  }) => {
    const { sitemodelHistory = [], persistentSelection } = locationState || {}
    const targetPath = `/${window.selectAndLinkFilterSitemodel}?uuid_objectclass=${objectclassUuid}`
    const currentPath = {
      url: location.pathname + location.search,
      targetField,
      targetGroup,
      targetComponent,
      filterState: persistentSelection?.FilterFields || filterState
    }

    return () => {
      navigateMergingState(targetPath, {
        state: {
          sitemodelHistory: [...sitemodelHistory, currentPath],
          shouldResetFilters: true,
          shouldResetSelectedOptions: true,
          shouldPersistData: false,
          selectedOptions
        }
      })
    }
  }

  const selectAndLink = (values: YesObject[]) => {
    if (!shouldRenderSelectAndLink()) {
      return
    }

    const {
      sitemodelHistory = [],
      persistentSelection: previousPersistentSelection
    } = locationState || {}
    const path = sitemodelHistory.pop()

    if (!path) {
      window.parent.postMessage(
        {
          type: SELECT_AND_LINK_POST_MESSAGE_TYPE,
          values
        },
        '*'
      )

      return
    }

    const persistentSelection = getPersistentSelection({
      previousPersistentSelection,
      targetField: path.targetField,
      targetGroup: path.targetGroup,
      targetComponent: path.targetComponent,
      groupedFieldValues: path.filterState,
      values
    })

    navigateMergingState(path.url, {
      state: {
        sitemodelHistory,
        persistentSelection,
        targetComponent: path.targetComponent,
        targetField: path.targetField,
        targetGroup: path.targetGroup,
        shouldPersistData: true,
        shouldResetFilters: false,
        shouldResetSelectedOptions: false
      }
    })
  }

  const navigate = useNavigate()

  const cancelSelectAndLink = () => {
    const { sitemodelHistory = [] } = locationState || {}
    const path = sitemodelHistory.pop()

    if (!path) {
      window.parent.postMessage(
        {
          type: CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE
        },
        '*'
      )

      return
    }

    navigate(-1)
  }

  return {
    selectAndLink,
    cancelSelectAndLink,
    getRedirectToObjectclassCallback,
    shouldRenderSelectAndLink
  }
}
